import { compose, keyBy } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, option, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import {
    createReducer,
    createAsyncActionTypes,
    composeProps,
    createPropsExtender,
    DateTimeUtcService,
    DateTime,
} from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { getProp, getStringProp } from '@mews/fp';
import { TimeUnitPeriod } from '@mews-commander/data-access';
import {
    createFetchAction,
    doesUtcIntervalContain,
    dateTimeToUtc,
    getFirstDayOfMonthUtc,
    getLastDayOfMonthUtc,
} from '@mews-commander/utils';
import prefix from '../prefix';
import moduleSelector from '../selector';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '../selectors/session/enterprise';

// @ts-expect-error $TSFixMeError: Argument of type '({ startUtc }: { startUtc: any; }) => any' is not assignable to parameter of type '(b: unknown) => any...
const calendarEventOrd = ord.contramap(({ startUtc }) => startUtc)(string.Ord);

export const NAME = 'calendarEvents';

export const CALENDAR_EVENT_FETCH = prefix('CALENDAR_EVENT_FETCH');
export const [CALENDAR_EVENT_FETCH_STARTED, CALENDAR_EVENT_FETCH_SUCCEEDED, CALENDAR_EVENT_FETCH_FAILED] =
    createAsyncActionTypes(CALENDAR_EVENT_FETCH);

export const fetchCalendarEvents =
    (startUtc: $TSFixMe, endUtc: $TSFixMe, enterpriseId: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .calendarEventComponent.getAll({ startUtc, endUtc, enterpriseId })
            .then((events: $TSFixMe) => keyBy('id')(events));
        return dispatch(createFetchAction(CALENDAR_EVENT_FETCH, promise));
    };

export const calendarEventsReducer = createReducer(
    {},
    {
        // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
        [CALENDAR_EVENT_FETCH_SUCCEEDED]: (state, payload) => ({
            ...state,
            ...payload,
        }),
    }
);

export const getCalendarEvents = createSelector(moduleSelector, (module) => module[NAME] || {});

export const getSortedCalendarEvents = createSelector(
    getCalendarEvents,
    flow(Object.values, array.sort(calendarEventOrd))
);

const getCalculatedCalendarEvents = ({ dateStartUtc, calendarEvents, dateEndUtc }: $TSFixMe) =>
    pipe(
        calendarEvents,
        Object.values,
        array.filterMap(({ startUtc, endUtc, ...other }) =>
            doesUtcIntervalContain({ startUtc, endUtc }, { startUtc: dateStartUtc, endUtc: dateEndUtc })
                ? option.some({
                      startUtc,
                      endUtc,
                      ...other,
                  })
                : option.none
        )
    );

export const getCalendarEventsByUtcString = createSelector(
    getStringProp('utcString'),
    getStringProp('timeUnit'),
    getStringProp('timeUnitPeriod'),
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getSortedCalendarEvents,
    (dateStartUtc, timeUnit, timeUnitPeriod, timezone: string, calendarEvents) => {
        if (timeUnit === TimeUnit.Month && timeUnitPeriod === TimeUnitPeriod.Month) {
            return getCalculatedCalendarEvents({
                dateStartUtc: getFirstDayOfMonthUtc(dateStartUtc, timezone),
                calendarEvents,
                dateEndUtc: getLastDayOfMonthUtc(dateStartUtc, timezone),
            });
        }

        return getCalculatedCalendarEvents({
            dateStartUtc,
            calendarEvents,
            dateEndUtc: compose(
                DateTimeUtcService.toISOString,
                (startDateTimeUtc) => DateTimeUtcService.addDays(startDateTimeUtc, 1),
                DateTimeUtcService.parse
            )(dateStartUtc),
        });
    }
);

const getDateInUtcString = createSelector(getProp<DateTime>()('date'), dateTimeToUtc);

export const getCalendarEventsByDateTime = composeProps(
    getCalendarEventsByUtcString,
    createPropsExtender('utcString', getDateInUtcString)
);
